/** Only top-level binary arguments are used by our protocols. JSON arguments
 * retain Socket.IO's JSON semantics; binary tags cannot collide with user data. */
export function encodeArguments(args: readonly unknown[]): Uint8Array {
  const encoded = args.map((value) => {
    const bytes = asBytes(value);

    if (!bytes) {
      return ["json", value];
    }

    let binary = "";

    for (let i = 0; i < bytes.length; i += 8192) {
      binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
    }

    return ["bytes", btoa(binary)];
  });
  return new TextEncoder().encode(JSON.stringify(encoded));
}

export function decodeArguments(bytes: Uint8Array): unknown[] {
  const values: unknown = JSON.parse(
    new TextDecoder("utf-8", { fatal: true }).decode(bytes),
  );

  if (!Array.isArray(values) || values.length > 16) {
    throw new Error("Invalid arguments");
  }

  return values.map((entry: unknown) => {
    if (!Array.isArray(entry) || entry.length !== 2) {
      throw new Error("Invalid argument");
    }

    if (entry[0] === "json") {
      return entry[1] as unknown;
    }

    if (entry[0] !== "bytes" || typeof entry[1] !== "string") {
      throw new Error("Invalid binary argument");
    }

    const binary = atob(entry[1]);
    return Uint8Array.from(binary, (char) => char.charCodeAt(0));
  });
}

export function asBytes(value: unknown): Uint8Array | undefined {
  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value);
  }

  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }

  return undefined;
}
